
import { Module } from '../types';

export const FORMS_MODULE: Module = {
  id: "intermediate-forms",
  title: "Forms and Form Handling",
  description: "Learn to build and manage forms in React with proper validation and user experience",
  duration: "75 minutes",
  difficulty: "intermediate",
  content: `
# Forms and Form Handling in React

Learn how to build robust, user-friendly forms in React with proper state management, validation, and accessibility.

## Controlled vs Uncontrolled Components

### Controlled Components (Recommended)
Form inputs whose values are controlled by React state.

\`\`\`jsx
function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Process form data
    console.log('Form submitted:', formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <input
        type="text"
        name="name"
        value={formData.name}
        onChange={handleChange}
        placeholder="Your Name"
        required
      />
      <input
        type="email"
        name="email"
        value={formData.email}
        onChange={handleChange}
        placeholder="Your Email"
        required
      />
      <textarea
        name="message"
        value={formData.message}
        onChange={handleChange}
        placeholder="Your Message"
        required
      />
      <button type="submit">Send Message</button>
    </form>
  );
}
\`\`\`

## Form Validation

### Basic Client-Side Validation
\`\`\`jsx
function RegistrationForm() {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Form is valid, submit data
      console.log('Registration successful:', formData);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div>
        <input
          type="text"
          name="username"
          value={formData.username}
          onChange={handleChange}
          placeholder="Username"
        />
        {errors.username && <span className="error">{errors.username}</span>}
      </div>
      
      <div>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Email"
        />
        {errors.email && <span className="error">{errors.email}</span>}
      </div>
      
      <div>
        <input
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="Password"
        />
        {errors.password && <span className="error">{errors.password}</span>}
      </div>
      
      <div>
        <input
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          placeholder="Confirm Password"
        />
        {errors.confirmPassword && <span className="error">{errors.confirmPassword}</span>}
      </div>
      
      <button type="submit">Register</button>
    </form>
  );
}
\`\`\`

## Custom Form Hook

\`\`\`jsx
function useForm(initialValues, validationRules) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;
    
    setValues(prev => ({
      ...prev,
      [name]: inputValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    Object.keys(validationRules).forEach(field => {
      const rule = validationRules[field];
      const value = values[field];
      
      if (rule.required && (!value || !value.toString().trim())) {
        newErrors[field] = \`\${field} is required\`;
      } else if (rule.minLength && value.length < rule.minLength) {
        newErrors[field] = \`\${field} must be at least \${rule.minLength} characters\`;
      } else if (rule.pattern && !rule.pattern.test(value)) {
        newErrors[field] = rule.message || \`\${field} is invalid\`;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (onSubmit) => (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    if (validate()) {
      onSubmit(values);
    }
    
    setIsSubmitting(false);
  };

  return {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleSubmit
  };
}

// Usage
function LoginForm() {
  const { values, errors, isSubmitting, handleChange, handleSubmit } = useForm(
    { email: '', password: '' },
    {
      email: {
        required: true,
        pattern: /\S+@\S+\.\S+/,
        message: 'Please enter a valid email'
      },
      password: {
        required: true,
        minLength: 6
      }
    }
  );

  const onSubmit = async (formData) => {
    try {
      // Submit to API
      await loginUser(formData);
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <input
        type="email"
        name="email"
        value={values.email}
        onChange={handleChange}
        placeholder="Email"
      />
      {errors.email && <span className="error">{errors.email}</span>}
      
      <input
        type="password"
        name="password"
        value={values.password}
        onChange={handleChange}
        placeholder="Password"
      />
      {errors.password && <span className="error">{errors.password}</span>}
      
      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Logging in...' : 'Login'}
      </button>
    </form>
  );
}
\`\`\`

## Best Practices

1. **Use controlled components** for better state management
2. **Validate on both client and server** for security
3. **Provide immediate feedback** - validate as users type
4. **Handle loading states** during form submission
5. **Make forms accessible** with proper labels and ARIA attributes
6. **Use proper input types** (email, password, number, etc.)
  `,
  codeExamples: [
    {
      title: "Controlled Input Component",
      code: `const [value, setValue] = useState('');

<input 
  type="text"
  value={value}
  onChange={(e) => setValue(e.target.value)}
  placeholder="Enter text"
/>`,
      explanation: "Basic pattern for controlled components in React forms",
      language: "jsx"
    }
  ],
  practice: {
    task: "Create a user registration form with validation for username, email, and password",
    starter: `function RegistrationForm() {
  // TODO: Add state for form data and validation
  
  return (
    <form>
      {/* TODO: Add form inputs with validation */}
    </form>
  );
}`,
    solution: `function RegistrationForm() {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: ''
  });
  
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      console.log('Registration successful:', formData);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div>
        <input
          type="text"
          name="username"
          value={formData.username}
          onChange={handleChange}
          placeholder="Username"
        />
        {errors.username && <span className="error">{errors.username}</span>}
      </div>
      
      <div>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Email"
        />
        {errors.email && <span className="error">{errors.email}</span>}
      </div>
      
      <div>
        <input
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="Password"
        />
        {errors.password && <span className="error">{errors.password}</span>}
      </div>
      
      <button type="submit">Register</button>
    </form>
  );
}`,
    hints: [
      "Use controlled components with value and onChange",
      "Validate each field and store errors in state",
      "Clear errors when user starts typing",
      "Prevent form submission if validation fails"
    ],
    estimatedTime: 35
  },
  quickCheck: {
    questions: [
      {
        id: "forms-q1",
        question: "What is a controlled component in React forms?",
        options: [
          "A component that controls other components",
          "A form input whose value is controlled by React state",
          "A component with validation",
          "A component that submits forms"
        ],
        correctAnswer: 1,
        correctAnswerIndex: 1,
        difficulty: "easy"
      },
      {
        id: "forms-q2",
        question: "Why should you call preventDefault() in form submission handlers?",
        options: [
          "To validate the form",
          "To prevent the default browser form submission behavior",
          "To clear form data",
          "To show loading state"
        ],
        correctAnswer: 1,
        correctAnswerIndex: 1,
        difficulty: "medium"
      },
      {
        id: "forms-q3",
        question: "When should you validate form data?",
        options: [
          "Only on form submission",
          "Only when the user types",
          "Both on input change and form submission",
          "Only on the server"
        ],
        correctAnswer: 2,
        correctAnswerIndex: 2,
        difficulty: "medium"
      }
    ]
  }
};
